import { useState, useRef, useCallback, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import type { PaymentAmounts } from "nautika-types";

interface PaymentSectionProps {
  promotionDiscount?: number; // Montant de la réduction de la promotion
  onPaymentChange?: (payments: PaymentAmounts) => void;
  onSubtotalChange?: (subtotal: number) => void; // Callback pour le sous-total avant promo
  hideDescription?: boolean; // Pour masquer la description
  hideSubtotalColumn?: boolean; // Masquer la colonne sous-total
  subtotalValue?: number; // Valeur du sous-total provenant du parent
  /** Valeurs initiales des paiements pour préremplissage */
  initialPayments?: PaymentAmounts;
  readOnly?: boolean; // Afficher en lecture seule sans modification
}

export const PaymentSection = function PaymentSection({
  promotionDiscount = 0,
  onPaymentChange,
  onSubtotalChange,
  hideDescription = false,
  hideSubtotalColumn = false,
  subtotalValue = 0,
  initialPayments = {
    card: 0,
    cash: 0,
    check: 0,
    ancv: 0,
  },
  readOnly = false,
}: PaymentSectionProps) {
  const [payments, setPayments] = useState<PaymentAmounts>(() => ({
    card: initialPayments.card ?? 0,
    cash: initialPayments.cash ?? 0,
    check: initialPayments.check ?? 0,
    ancv: initialPayments.ancv ?? 0,
  }));

  const [subtotalAmount, setSubtotalAmount] = useState<number>(() => (hideSubtotalColumn ? 0 : subtotalValue || 0));

  // Références pour éviter les boucles infinies
  const lastNotifiedPayments = useRef<PaymentAmounts>({ card: 0, cash: 0, check: 0, ancv: 0 });
  const lastNotifiedSubtotal = useRef<number>(0);
  // Note: initialization handled via lazy state initializers above
  const onPaymentChangeRef = useRef(onPaymentChange);
  const onSubtotalChangeRef = useRef(onSubtotalChange);

  // Mettre à jour les refs des callbacks après le render
  useEffect(() => {
    onPaymentChangeRef.current = onPaymentChange;
  }, [onPaymentChange]);

  useEffect(() => {
    onSubtotalChangeRef.current = onSubtotalChange;
  }, [onSubtotalChange]);

  // Références pour la navigation entre les champs
  const subtotalRef = useRef<HTMLInputElement>(null);
  const cbRef = useRef<HTMLInputElement>(null);
  const cashRef = useRef<HTMLInputElement>(null);
  const chequeRef = useRef<HTMLInputElement>(null);
  const chequeVacancesRef = useRef<HTMLInputElement>(null);

  // États pour les valeurs d'affichage (permet la saisie de décimales)
  const [displayValues, setDisplayValues] = useState<{ [key: string]: string }>(() => ({
    subtotal: hideSubtotalColumn ? "0" : (subtotalValue || 0).toString(),
    card: (initialPayments.card || 0).toString(),
    cash: (initialPayments.cash || 0).toString(),
    check: (initialPayments.check || 0).toString(),
    ancv: (initialPayments.ancv || 0).toString(),
  }));

  // Fonction pour sélectionner tout le texte lors du focus
  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  const totalPaid = (payments.card ?? 0) + (payments.cash ?? 0) + (payments.check ?? 0) + (payments.ancv ?? 0);
  const effectiveSubtotal = hideSubtotalColumn ? subtotalValue : subtotalAmount;
  const totalAfterPromotion = effectiveSubtotal - promotionDiscount; // Calcul du total après promotion
  const remainingAmount = totalAfterPromotion - totalPaid;

  // Notifier le parent des changements de paiement de manière asynchrone
  useEffect(() => {
    const current = payments;
    const last = lastNotifiedPayments.current;

    // Vérifier si les valeurs ont réellement changé
    if (
      (current.card ?? 0) !== (last.card ?? 0) ||
      (current.cash ?? 0) !== (last.cash ?? 0) ||
      (current.check ?? 0) !== (last.check ?? 0) ||
      (current.ancv ?? 0) !== (last.ancv ?? 0)
    ) {
      lastNotifiedPayments.current = { ...current };
      onPaymentChangeRef.current?.(current);
    }
  }, [payments]);

  // Notifier le parent des changements de sous-total de manière asynchrone
  useEffect(() => {
    if (!hideSubtotalColumn && subtotalAmount !== lastNotifiedSubtotal.current) {
      lastNotifiedSubtotal.current = subtotalAmount;
      onSubtotalChangeRef.current?.(subtotalAmount);
    }
  }, [subtotalAmount, hideSubtotalColumn]);

  // Fonction pour naviguer vers le champ suivant
  const handleKeyDown = useCallback((e: React.KeyboardEvent, nextFieldRef?: React.RefObject<HTMLInputElement | null>) => {
    if (e.key === "Enter" && nextFieldRef?.current) {
      e.preventDefault();
      nextFieldRef.current.focus();
    }
  }, []);

  const handlePaymentChange = useCallback(
    (method: keyof PaymentAmounts, value: string) => {
      // Mettre à jour la valeur d'affichage immédiatement
      setDisplayValues((prev) => ({
        ...prev,
        [method]: value,
      }));

      // Permettre une chaîne vide
      if (value === "") {
        setPayments((prev) => ({
          ...prev,
          [method]: 0,
        }));
        return;
      }

      // Permettre les caractères de saisie intermédiaires pour les décimaux
      const cleanValue = value.replace(",", ".");

      // Vérifier si c'est un pattern valide en cours de saisie
      const isValidPattern = /^(\d+\.?\d*|\.\d+)$/.test(cleanValue);

      if (!isValidPattern) {
        // Revenir à la valeur précédente si le pattern n'est pas valide
        const currentValue = payments[method] ?? 0;
        setDisplayValues((prev) => ({
          ...prev,
          [method]: currentValue === 0 ? "" : currentValue.toString(),
        }));
        return;
      }

      // Si ça se termine par un point, on garde la chaîne pour permettre la saisie
      if (cleanValue.endsWith(".")) {
        // On ne met pas à jour la valeur numérique, juste l'affichage
        return;
      }

      const numericValue = parseFloat(cleanValue);

      // Vérifier que c'est un nombre valide et positif
      if (isNaN(numericValue) || numericValue < 0) {
        const currentValue = payments[method] ?? 0;
        setDisplayValues((prev) => ({
          ...prev,
          [method]: currentValue === 0 ? "" : currentValue.toString(),
        }));
        return;
      }

      setPayments((prev) => ({
        ...prev,
        [method]: numericValue,
      }));
    },
    [payments]
  );

  const handleSubtotalChange = useCallback(
    (value: string) => {
      // Mettre à jour la valeur d'affichage immédiatement
      setDisplayValues((prev) => ({
        ...prev,
        subtotal: value,
      }));

      // Permettre une chaîne vide
      if (value === "") {
        setSubtotalAmount(0);
        return;
      }

      // Permettre les caractères de saisie intermédiaires pour les décimaux
      const cleanValue = value.replace(",", ".");

      // Vérifier si c'est un pattern valide en cours de saisie
      const isValidPattern = /^(\d+\.?\d*|\.\d+)$/.test(cleanValue);

      if (!isValidPattern) {
        // Revenir à la valeur précédente si le pattern n'est pas valide
        setDisplayValues((prev) => ({
          ...prev,
          subtotal: subtotalAmount === 0 ? "" : subtotalAmount.toString(),
        }));
        return;
      }

      // Si ça se termine par un point, on garde la chaîne pour permettre la saisie
      if (cleanValue.endsWith(".")) {
        return;
      }

      const numericValue = parseFloat(cleanValue);

      // Vérifier que c'est un nombre valide et positif
      if (isNaN(numericValue) || numericValue < 0) {
        setDisplayValues((prev) => ({
          ...prev,
          subtotal: subtotalAmount === 0 ? "" : subtotalAmount.toString(),
        }));
        return;
      }

      setSubtotalAmount(numericValue);
    },
    [subtotalAmount]
  );

  const formatCurrency = (amount: number) => {
    // Si le montant est un entier, ne pas afficher les décimales
    if (amount % 1 === 0) {
      return `${amount}€`;
    }
    // Sinon, afficher avec 2 décimales
    return `${amount.toFixed(2)}€`;
  };

  // Removed initialization effect to satisfy React Compiler; values are lazily initialized above.

  return (
    <div>
      <div className="flex flex-col gap-y-2">
        {!hideDescription && (
          <>
            <div className="text-lg font-semibold">Mode de paiement</div>
            <div className="text-sm text-gray-700">Indiquez ici les montants réglés selon le mode de paiement choisi</div>
          </>
        )}

        {/* Tableau en CSS Grid */}
        <div className={`${hideDescription ? "" : "rounded-lg border"}`}>
          {/* Header */}
          <div className={`grid ${hideSubtotalColumn ? "grid-cols-6" : "grid-cols-7"} border-b bg-gray-100`}>
            <div className="border-r p-3 text-center font-medium"></div>
            {!hideSubtotalColumn && (
              <div className="border-r p-3 text-center font-medium">
                <div>Sous-total</div>
                <div> avant promo</div>
              </div>
            )}
            <div className="border-r p-3 text-center font-medium">CB</div>
            <div className="border-r p-3 text-center font-medium">Espèces</div>
            <div className="border-r p-3 text-center font-medium">Chèques</div>
            <div className="border-r p-3 text-center font-medium">Chèques vac</div>
            <div className="p-3 text-center font-medium">Total</div>
          </div>

          {/* Row */}
          <div className={`grid bg-white ${hideSubtotalColumn ? "grid-cols-6" : "grid-cols-7"} items-start`}>
            <div className="flex h-full items-center justify-center border-r p-3 text-center text-sm font-medium text-gray-600 md:text-lg">Montant</div>
            {!hideSubtotalColumn && (
              <div className="flex h-full items-start justify-center border-r p-3">
                {readOnly ? (
                  <div className="flex items-center justify-center">
                    <span className="text-center">{formatCurrency(effectiveSubtotal)}</span>
                  </div>
                ) : (
                  <>
                    <Input
                      disabled={readOnly}
                      ref={subtotalRef}
                      type="text"
                      value={displayValues.subtotal}
                      onChange={(e) => handleSubtotalChange(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, cbRef)}
                      onFocus={handleInputFocus}
                      placeholder="0.00"
                      className="text-center shadow-none placeholder:focus:opacity-0 focus-visible:ring-0"
                    />
                    <span className="text-md ml-1">€</span>
                  </>
                )}
              </div>
            )}
            <div className="flex h-full items-center justify-center border-r p-3">
              {readOnly ? (
                <div className="flex h-full items-center justify-center">
                  <span className="text-center text-sm md:text-lg">{formatCurrency(payments.card ?? 0)}</span>
                </div>
              ) : (
                <>
                  <Input
                    ref={cbRef}
                    type="text"
                    value={displayValues.card}
                    onChange={(e) => handlePaymentChange("card", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, cashRef)}
                    onFocus={handleInputFocus}
                    placeholder="0.00"
                    className="text-center shadow-none placeholder:focus:opacity-0 focus-visible:ring-0"
                  />
                  <span className="text-md ml-1">€</span>
                </>
              )}
            </div>
            <div className="h-full border-r p-3">
              <div className="flex h-full items-center justify-center gap-1">
                {readOnly ? (
                  <span className="text-center text-sm md:text-lg">{formatCurrency(payments.cash ?? 0)}</span>
                ) : (
                  <>
                    <Input
                      ref={cashRef}
                      type="text"
                      value={displayValues.cash}
                      onChange={(e) => handlePaymentChange("cash", e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, chequeRef)}
                      onFocus={handleInputFocus}
                      placeholder="0.00"
                      className="text-center shadow-none placeholder:focus:opacity-0 focus-visible:ring-0"
                    />
                    <span className="text-md ml-1">€</span>
                  </>
                )}
              </div>
            </div>
            <div className="h-full border-r p-3">
              <div className="flex h-full items-center justify-center gap-1">
                {readOnly ? (
                  <span className="text-center text-sm md:text-lg">{formatCurrency(payments.check ?? 0)}</span>
                ) : (
                  <>
                    <Input
                      ref={chequeRef}
                      type="text"
                      value={displayValues.check}
                      onChange={(e) => handlePaymentChange("check", e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, chequeVacancesRef)}
                      onFocus={handleInputFocus}
                      placeholder="0.00"
                      className="text-center shadow-none placeholder:focus:opacity-0 focus-visible:ring-0"
                    />
                    <span className="text-md ml-1">€</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex h-full items-center justify-center border-r p-3">
              {readOnly ? (
                <div className="flex items-center justify-center">
                  <span className="text-center text-sm md:text-lg">{formatCurrency(payments.ancv ?? 0)}</span>
                </div>
              ) : (
                <>
                  <Input
                    ref={chequeVacancesRef}
                    type="text"
                    value={displayValues.ancv}
                    onChange={(e) => handlePaymentChange("ancv", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, subtotalRef)} // Retourne au premier champ pour faire une boucle
                    onFocus={handleInputFocus}
                    placeholder="0.00"
                    className="text-center shadow-none placeholder:focus:opacity-0 focus-visible:ring-0"
                  />
                  <span className="text-md ml-1">€</span>
                </>
              )}
            </div>
            <div className="flex h-full flex-col gap-1 p-3">
              <div className="flex items-center justify-center gap-1">
                <span className="text-center text-sm md:text-lg">{formatCurrency(totalAfterPromotion)}</span>
              </div>
              <div className="items-between flex justify-center">
                <Badge
                  variant={effectiveSubtotal === 0 ? "destructive" : remainingAmount === 0 ? "default" : remainingAmount > 0 ? "destructive" : "secondary"}
                  className={
                    effectiveSubtotal === 0
                      ? ""
                      : remainingAmount === 0
                        ? "bg-green-500 hover:bg-green-600"
                        : remainingAmount < 0
                          ? "bg-blue-500 text-white hover:bg-blue-600"
                          : ""
                  }
                >
                  {effectiveSubtotal === 0
                    ? "Prix non renseigné"
                    : remainingAmount === 0
                      ? "Paiement complet"
                      : remainingAmount > 0
                        ? `${formatCurrency(remainingAmount)} manquant`
                        : `${formatCurrency(Math.abs(remainingAmount))} surplus`}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
